"use strict";
/*
 * Pure analysis helpers for scroll bot detection.
 *
 * The input is a list of scroll samples: the position (window.scrollY, in pixels)
 * and the time it was seen (in milliseconds), in order. Real scrolling is uneven
 * and limited by the input device. Scripts tend to move by identical steps at an
 * identical rhythm, or jump a huge distance at once.
 *
 * All thresholds are rough guesses, meant to be tuned with real traffic.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.hasUniformScroll = exports.hasLargeScrollJump = exports.MIN_SCROLL_EVENTS_FOR_ANALYSIS = void 0;
const helpers_1 = require("lib/bots/helpers");
// Fewest real moves we need before the even-step check makes sense.
exports.MIN_SCROLL_EVENTS_FOR_ANALYSIS = 6;
// Step-size variation (a unitless ratio) below this looks machine-even.
const EVEN_STEP_SIZE_RATIO_THRESHOLD = 0.05;
// Step-timing variation (a unitless ratio) below this looks machine-even. A real
// mouse wheel gives even step sizes but human-variable timing, so requiring both
// avoids flagging wheel users.
const EVEN_STEP_TIMING_RATIO_THRESHOLD = 0.05;
// A single scroll jump bigger than this many pixels is beyond human input.
const MAX_HUMAN_SCROLL_JUMP_PX = 2000;
// The size (px) and time gap (ms) of each real move between consecutive samples.
// Zero-size steps are dropped so a pause does not count as a move.
const getScrollMoves = (samples) => {
    const moves = [];
    for (let index = 1; index < samples.length; index += 1) {
        const sizePx = Math.abs(samples[index].positionPx - samples[index - 1].positionPx);
        if (sizePx === 0) {
            continue;
        }
        moves.push({ sizePx, gapMs: Math.max(samples[index].timeMs - samples[index - 1].timeMs, 1) });
    }
    return moves;
};
// True if a single scroll event jumped further than a human could.
const hasLargeScrollJump = (samples) => {
    return getScrollMoves(samples).some((move) => move.sizePx > MAX_HUMAN_SCROLL_JUMP_PX);
};
exports.hasLargeScrollJump = hasLargeScrollJump;
// True if scrolling is machine-even in BOTH how far each step moves AND how
// evenly the steps are spaced in time.
const hasUniformScroll = (samples) => {
    const moves = getScrollMoves(samples);
    if (moves.length < exports.MIN_SCROLL_EVENTS_FOR_ANALYSIS) {
        return false;
    }
    const sizesAreEven = (0, helpers_1.coefficientOfVariation)(moves.map((move) => move.sizePx)) < EVEN_STEP_SIZE_RATIO_THRESHOLD;
    const timingIsEven = (0, helpers_1.coefficientOfVariation)(moves.map((move) => move.gapMs)) < EVEN_STEP_TIMING_RATIO_THRESHOLD;
    return sizesAreEven && timingIsEven;
};
exports.hasUniformScroll = hasUniformScroll;
